import { Box, Menu } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useCurrentUser from "../../../hooks/useCurrentUser";
import {
  selectExpertUserById,
  updateExpertOutOfOffice,
} from "../../user/expertUsersSlice";
import { ExpertOutOfOfficePeriod } from "../../user/types/entities";
import { signOut } from "../../user/userSlice";
import LanguageSelection from "./LanguageSelection";
import LogOut from "./LogOut";
import StatusMessageForm from "./StatusMessageForm";
import {
  hasScheduledStatusMessage,
  outOfOfficeVersion,
} from "./statusMessageMappers";
import StatusMessageMenuItem from "./StatusMessageMenuItem";
import UserInfo from "./UserInfo";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  fullName: string;
  username: string;
};

type MenuView = "main" | "statusMessage";

const ToolbarMenu = ({ anchorEl, onClose, fullName, username }: Props) => {
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.language;
  const dispatch = useAppDispatch();
  const { id: currentUserId } = useCurrentUser();
  const expert = useAppSelector((state) =>
    selectExpertUserById(state, currentUserId),
  );

  const [view, setView] = useState<MenuView>("main");
  const [isLoading, setIsLoading] = useState(false);

  const menuOpen = Boolean(anchorEl);
  const outOfOffice = expert?.account.outOfOffice;
  const statusMessageSet = hasScheduledStatusMessage(outOfOffice);

  useEffect(() => {
    if (!menuOpen) {
      setView("main");
      setIsLoading(false);
    }
  }, [menuOpen]);

  const handleSignOut = async () => dispatch(signOut());

  const handleLanguageChange = (newValue: string) => {
    i18n.changeLanguage(newValue);
  };

  const handleMenuClose = (_event: object, reason: string) => {
    if (reason === "backdropClick" && view === "statusMessage") return;

    onClose();
  };

  const handleSave = (nextOutOfOffice: ExpertOutOfOfficePeriod) => {
    if (!currentUserId) return;

    setIsLoading(true);
    dispatch(
      updateExpertOutOfOffice({
        expertId: currentUserId,
        ...nextOutOfOffice,
        version: outOfOfficeVersion(expert?.account.outOfOffice),
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setView("main");
      }
      setIsLoading(false);
    });
  };

  const handleDelete = () => {
    if (!currentUserId) return;

    setIsLoading(true);
    dispatch(
      updateExpertOutOfOffice({
        expertId: currentUserId,
        version: outOfOfficeVersion(expert?.account.outOfOffice),
        message: null,
        from: null,
        until: null,
      }),
    ).then(() => {
      setIsLoading(false);
    });
  };

  return (
    <Menu
      disableAutoFocus={view === "statusMessage"}
      disableEnforceFocus={view === "statusMessage"}
      disableRestoreFocus={view === "statusMessage"}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          width: "350px",
          borderRadius: "8px",
          overflow: view === "statusMessage" ? "visible !important" : undefined,
        },
      }}
      MenuListProps={{
        autoFocusItem: view === "main" && !isLoading,
        sx: {
          overflow: view === "statusMessage" ? "visible !important" : undefined,
        },
      }}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={menuOpen}
    >
      <Box position="relative">
        <OverlaySpinner sx={{ position: "absolute" }} isLoading={isLoading} />
        {view === "statusMessage" ? (
          <StatusMessageForm
            isLoading={isLoading}
            onCancel={() => setView("main")}
            onSave={handleSave}
            outOfOffice={statusMessageSet ? outOfOffice : null}
          />
        ) : (
          <>
            <UserInfo fullName={fullName} username={username} />
            <StatusMessageMenuItem
              disabled={isLoading}
              hasStatusMessage={statusMessageSet}
              onDelete={handleDelete}
              onOpen={() => setView("statusMessage")}
            />
            <LanguageSelection
              selectedLanguage={selectedLanguage}
              handleLanguageChange={handleLanguageChange}
            />
            <LogOut onClick={handleSignOut} label={t("common:signOut")} />
          </>
        )}
      </Box>
    </Menu>
  );
};

export default ToolbarMenu;
